import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { priceTablesAdminApi, type TestPriceResult } from "@/lib/price-tables";

export interface TestPricePanelProps { priceTableId: number; rateTypes: string[]; }

export function TestPricePanel({ priceTableId, rateTypes }: TestPricePanelProps) {
  const [quantity, setQuantity] = useState(50);
  const [axis, setAxis] = useState<"Colours" | "Positions" | "Fixed">("Colours");
  const [colours, setColours] = useState<number | "">("");
  const [positions, setPositions] = useState<number | "">("");
  const [column, setColumn] = useState<number | "">("");
  const [rateType, setRateType] = useState("");
  const [result, setResult] = useState<TestPriceResult | null>(null);

  const run = useMutation({
    mutationFn: () => priceTablesAdminApi.test(priceTableId, {
      quantity,
      rateAxis: axis,
      rateColumn: axis === "Fixed" && column !== "" ? Number(column) : null,
      colours: colours === "" ? null : Number(colours),
      positions: positions === "" ? null : Number(positions),
      rateTypeCode: rateType || null,
    }),
    onSuccess: (r) => setResult(r),
  });

  return (
    <div className="border rounded p-3 space-y-2">
      <h3 className="font-medium text-sm">
        Test pricing{" "}
        <span className="text-xs text-muted-foreground">(uses saved rows — save edits first)</span>
      </h3>
      <div className="flex flex-wrap gap-3 items-end text-sm">
        <label htmlFor="tpp-quantity" className="flex flex-col">
          Quantity
          <Input
            id="tpp-quantity"
            type="number"
            className="w-24"
            value={quantity}
            onChange={(e) => setQuantity(Number(e.target.value))}
          />
        </label>
        <label htmlFor="tpp-axis" className="flex flex-col">
          Rate axis
          <select
            id="tpp-axis"
            className="border rounded h-9 px-2"
            value={axis}
            onChange={(e) => setAxis(e.target.value as typeof axis)}
          >
            <option>Colours</option>
            <option>Positions</option>
            <option>Fixed</option>
          </select>
        </label>
        {axis === "Colours" && (
          <label htmlFor="tpp-colours" className="flex flex-col">
            Colours
            <Input
              id="tpp-colours"
              type="number"
              className="w-20"
              value={colours}
              onChange={(e) =>
                setColours(e.target.value === "" ? "" : Number(e.target.value))
              }
            />
          </label>
        )}
        {axis === "Positions" && (
          <label htmlFor="tpp-positions" className="flex flex-col">
            Positions
            <Input
              id="tpp-positions"
              type="number"
              className="w-20"
              value={positions}
              onChange={(e) =>
                setPositions(e.target.value === "" ? "" : Number(e.target.value))
              }
            />
          </label>
        )}
        {axis === "Fixed" && (
          <label htmlFor="tpp-column" className="flex flex-col">
            Column (1-9)
            <Input
              id="tpp-column"
              type="number"
              className="w-20"
              value={column}
              onChange={(e) =>
                setColumn(e.target.value === "" ? "" : Number(e.target.value))
              }
            />
          </label>
        )}
        <label className="flex flex-col">
          Rate type
          <select
            aria-label="Rate type filter"
            className="border rounded h-9 px-2"
            value={rateType}
            onChange={(e) => setRateType(e.target.value)}
          >
            <option value="">Any</option>
            {rateTypes.map((rt) => (
              <option key={rt} value={rt}>
                {rt}
              </option>
            ))}
          </select>
        </label>
        <Button type="button" onClick={() => run.mutate()}>
          Run test
        </Button>
      </div>
      {result && (
        <p className="text-sm">
          {result.value === null ? (
            "No matching row."
          ) : (
            <>
              Row <b>{result.rangeDesc ?? result.matchedRecId}</b> · Rate
              {result.rateColumn} = <b>{result.value}</b>
            </>
          )}
        </p>
      )}
    </div>
  );
}
